import './PushNode.scss';

import { JourneyComponentTypes } from 'pages/FlowBuilder/types/flow-builder.types';
import { PUSH_LABELS } from 'components/ExperienceCanvas/constants';
import { PushStateItem } from 'store/push/push.type';
import { useFlowBuilderContext } from 'pages/FlowBuilder/context/flow-builder.context';
import BaseNode, { BaseNodeProps } from 'components/FlowBuilder/FlowNodes/BaseNode/BaseNode';
import React, { useCallback, useMemo } from 'react';
import { PushPayloadType } from 'interface/push/push.interface';
import IconPush from 'pages/FlowBuilder/assets/icons/IconPush';
import { FlightTooltip } from '@flybits/design-system';
import OptionalNodeWrapper from '../OptionalNodeWrapper/OptionalNodeWrapper';
import { useThunkDispatch as useDispatch, useAppSelector as useSelector } from 'hooks/reduxHooks';
import useJourneyStatus from 'hooks/useJourneyStatus';
import { validateStepAction } from 'validators/ExperienceCanvas/journey.validator';
import { actionUsesOptionalWrapper } from 'pages/FlowBuilder/constants/flow-node-layout';
import { TOUCHPOINT_STATUSES } from 'components/ExperienceCanvas/types';
import { ERROR_TYPES } from 'pages/ExperienceCanvas/types';
import { getTooltipIcon } from 'pages/FlowBuilder/helpers/flow-builder.helpers';

import { FlowNodeTypes } from '../node.types';

const MAIN_CLASS = 'push-node';
const CLASSES = {
  LABEL: `${MAIN_CLASS}__label`,
  LABEL_DISABLED: `${MAIN_CLASS}__label--disabled`,
  LABEL_TEXT: `${MAIN_CLASS}__label__text`,
  LABEL_NAME: `${MAIN_CLASS}__label__name`,
  NAME_TOOLTIP: `${MAIN_CLASS}__name-tooltip`,
};

type PushNodeProps = {
  data: {
    push: PushStateItem;
    pushType: PushPayloadType;
    stepIndex: number;
  };
};

function PushNode(props: PushNodeProps) {
  const {
    data: { push, pushType, stepIndex },
  } = props;

  const { errorStatus, errorMessage } = useSelector((state) => {
    const { errorStatus, errors } = validateStepAction(state, stepIndex, FlowNodeTypes.PUSH);
    // currently only revealing the first error on a node.
    const errorMessage = errors[0]?.err ?? '';

    if (errorStatus === '') {
      return { errorStatus: TOUCHPOINT_STATUSES.COMPLETE, errorMessage };
    } else if (errorStatus === ERROR_TYPES.INCOMPLETE) {
      return { errorStatus: TOUCHPOINT_STATUSES.INCOMPLETE, errorMessage };
    } else {
      return { errorStatus: TOUCHPOINT_STATUSES.ERROR, errorMessage };
    }
  });

  const dispatch = useDispatch();
  const { isJourneyLocked } = useJourneyStatus();
  const flowBuilderContext = useFlowBuilderContext();
  const dependencyGraph = useSelector((state) => state.te.journey.dependencyGraph);

  const handleEditPush = useCallback(() => {
    flowBuilderContext.sidePanels.push.setStepIndex(stepIndex);
    flowBuilderContext.sidePanels.push.setPushContext({ isOpen: true, isReadonly: isJourneyLocked });
  }, [flowBuilderContext.sidePanels.push, stepIndex, isJourneyLocked]);

  const handleRenamePush = useCallback(() => {
    flowBuilderContext.modals.rename.setData({
      entity: push,
      entityType: JourneyComponentTypes.PUSH,
      stepIndex,
      pushType,
    });
  }, [flowBuilderContext.modals.rename, push, pushType, stepIndex]);

  const pushName = push.name || 'Push Notification';
  const pushLabel = pushType ? PUSH_LABELS[pushType] : '';
  const isOptional = push.isOptional;
  const isOptionalPushEnabled = !!push.enableOptionalNode;

  const handleToggleOptionalPush = () => {
    dispatch({
      type: 'UPDATE_PUSH_SIMPLE',
      payload: {
        refId: push.refId,
        fields: { enableOptionalNode: !isOptionalPushEnabled },
      },
    });
  };

  const baseNodeProps = useMemo<Omit<BaseNodeProps, 'children'>>(() => {
    const menuItems = isJourneyLocked
      ? [
          {
            icon: 'eyeOpenOutline',
            label: 'View Push Notification',
            // Touchpoint editor currently locks push anyway
            onClick: handleEditPush,
          },
        ]
      : [
          {
            icon: 'editOutline',
            label: 'Edit Push Notification',
            onClick: handleEditPush,
            iconRight: getTooltipIcon(errorStatus),
            iconRightTooltip: errorMessage,
          },
          {
            icon: 'textBox',
            label: 'Rename',
            onClick: handleRenamePush,
          },
        ];

    return {
      className: MAIN_CLASS,
      menuItems,
      Icon: IconPush,
    };
  }, [isJourneyLocked, handleEditPush, errorStatus, errorMessage, handleRenamePush]);

  const renderPushNode = () => (
    <div className={`${CLASSES.LABEL} ${isOptional && !isOptionalPushEnabled ? CLASSES.LABEL_DISABLED : ''}`}>
      <FlightTooltip description={`Push Name: ${pushName}`} className={CLASSES.NAME_TOOLTIP}>
        <div className={CLASSES.LABEL_TEXT}>{pushName}</div>
      </FlightTooltip>
      {pushLabel && <div className={CLASSES.LABEL_NAME}>{`Type: ${pushLabel}`}</div>}
    </div>
  );

  const showOptionalNodeWrapper = actionUsesOptionalWrapper(push.refId, isOptional, {
    isJourneyLocked,
    dependencyGraph,
  });

  return showOptionalNodeWrapper ? (
    <OptionalNodeWrapper isEnabled={isOptionalPushEnabled} onToggle={handleToggleOptionalPush}>
      <BaseNode {...baseNodeProps} isOptional isDisabled={!isOptionalPushEnabled} status={errorStatus}>
        {renderPushNode()}
      </BaseNode>
    </OptionalNodeWrapper>
  ) : (
    <BaseNode {...baseNodeProps} status={errorStatus}>
      {renderPushNode()}
    </BaseNode>
  );
}

export default PushNode;
